import { AdAccountDTO } from 'api-models'
import { AdAccounts } from 'services/user'
import { useAssetStore } from 'store'
import { ignoredCampaignIds } from './connection-helpers'

/**
 * Every write to `preferences.ignored_campaigns`, serialised per ad account
 * (DEV-2422 §2.3).
 *
 * The endpoint replaces the WHOLE list, so two campaigns of the same account written at
 * once is silent data loss: both build their payload from the list they rendered with,
 * and whichever lands second puts back the campaign the first had just removed. Only the
 * clicked row is disabled while it saves, so the second click is a normal thing to do.
 *
 * Two halves, and it needs both:
 *  - one promise chain per ad account, so the writes cannot overlap;
 *  - the list is read from the STORE inside the queued task — after the previous write's
 *    `refreshAdAccounts` has landed — rather than from the caller's render.
 *
 * Different ad accounts still run in parallel; they own different lists.
 */
const queues = new Map<string, Promise<unknown>>()

export const setCampaignDetection = (
	adAccount: AdAccountDTO,
	campaignId: string,
	detecting: boolean,
	refreshAdAccounts: () => Promise<void>
): Promise<void> => {
	const run = async () => {
		// The freshest copy this client holds, not the one the caller rendered with.
		const live =
			useAssetStore
				.getState()
				.adAccounts.find((entry) => entry.id === adAccount.id) ?? adAccount
		const current = ignoredCampaignIds(live)
		const isIgnored = current.includes(campaignId)
		// A queued write can arrive to find its work already done — the drawer's save and
		// the row toggle reach the same list.
		if (detecting === !isIgnored) return

		const next = detecting
			? current.filter((id) => id !== campaignId)
			: [...current, campaignId]

		await AdAccounts.updateIgnoredCampaigns(
			adAccount.account_id,
			adAccount.id,
			next
		)
		await refreshAdAccounts()
	}

	// Neutralised before chaining, so one rejected write does not cancel every later one.
	// The caller still sees its own failure, through the promise returned here.
	const queued = (queues.get(adAccount.id) ?? Promise.resolve())
		.catch(() => undefined)
		.then(run)

	const tail: Promise<unknown> = queued
		.catch(() => undefined)
		.then(() => {
			// Only the tail clears the entry: a later write may already have replaced it.
			if (queues.get(adAccount.id) === tail) queues.delete(adAccount.id)
		})
	queues.set(adAccount.id, tail)

	return queued
}
